import { useState, useEffect } from "react";
import { X, Loader2, Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api } from "../lib/api";
import posthog from "../lib/analytics";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  reason?: string;
  feature?: string;
  userPlan?: string;
}


interface LifetimeStats {
  totalShifts: number;
  totalEarned: number;
  totalHours: number;
}

export default function UpgradeModal({ isOpen, onClose, reason, feature, userPlan }: Props) {
  const { t } = useTranslation("common");
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<LifetimeStats | null>(null);
  const [monthlyEstimate, setMonthlyEstimate] = useState<number | null>(null);
  const FEATURE_COPY: Record<string, { headline: string; subtext: string }> = {
    unlimited_clients: {
      headline: t('upgrade.feature.unlimitedClients.headline', "You're growing"),
      subtext: t('upgrade.feature.unlimitedClients.subtext', "Pro removes the client limit so you can manage as many clients as you want."),
    },
    ai_summary: {
      headline: t('upgrade.feature.aiSummary.headline', "Families love these updates"),
      subtext: t('upgrade.feature.aiSummary.subtext', "Pro turns your shift notes into professional care summaries families actually read."),
    },
    voice_parse: {
      headline: t('upgrade.feature.voiceParse.headline', "Talk, don't type"),
      subtext: t('upgrade.feature.voiceParse.subtext', "Voice-to-shift fills in your care log from a quick voice note. Pro feature."),
    },
    weekly_trends: {
      headline: t('upgrade.feature.weeklyTrends.headline', "Prove your value every week"),
      subtext: t('upgrade.feature.weeklyTrends.subtext', "Weekly trend reports show families exactly what you did. The kind of reporting agencies charge for."),
    },
    invoice_limit: {
      headline: t('upgrade.feature.invoiceLimit.headline', "Invoice without limits"),
      subtext: t('upgrade.feature.invoiceLimit.subtext', "Pro gives you unlimited invoicing with payment reminders and tracking."),
    },
    real_rate: {
      headline: t('upgrade.feature.realRate.headline', "Know your real hourly rate"),
      subtext: t('upgrade.feature.realRate.subtext', "After taxes and drive costs, what do you actually earn? Pro shows you."),
    },
    clients: {
      headline: t('upgrade.feature.clients.headline', "Unlock unlimited clients"),
      subtext: t('upgrade.feature.clients.subtext', "Pro removes the 2-client limit."),
    },
    care_feed: {
      headline: t('upgrade.feature.careFeed.headline', "Share updates with family"),
      subtext: t('upgrade.feature.careFeed.subtext', "Pro lets you post care notes and photos that family members can read in real time."),
    },
    family_portal: {
      headline: t('upgrade.feature.familyPortal.headline', "Show family the full picture"),
      subtext: t('upgrade.feature.familyPortal.subtext', "Pro gives family members daily updates, mood tracking, schedule, and care plans. Not just summaries."),
    },
    care_history: {
      headline: t('upgrade.feature.careHistory.headline', "Families want this on file"),
      subtext: t('upgrade.feature.careHistory.subtext', "Care history reports compile every approved summary into a single PDF for care reviews."),
    },
    care_plan_pdf: {
      headline: t('upgrade.feature.carePlanPdf.headline', "Share the care plan"),
      subtext: t('upgrade.feature.carePlanPdf.subtext', "Export a client's care plan as a PDF to share with supervisors or family members."),
    },
  };

  useEffect(() => {
    if (isOpen) {
      try { posthog.capture("upgrade_modal_shown"); } catch { /* ignored */ }
      api("/api/stats/lifetime")
        .then((data: LifetimeStats) => setStats(data))
        .catch(() => {});
      api("/api/dashboard")
        .then((data: any) => {
          const monthly = Math.round((data.totalWeeklyEarnings || 0) * 4.3);
          setMonthlyEstimate(monthly);
        })
        .catch(() => {});
    }
  }, [isOpen, feature]);

  if (!isOpen) return null;

  const featureCopy = feature ? FEATURE_COPY[feature] : undefined;

  const isStarterUser = userPlan === "starter";

  const handleClose = () => {
    try { posthog.capture("upgrade_modal_dismissed", { feature: feature ?? "unknown" }); } catch { /* ignored */ }
    onClose();
  };

  const handleUpgrade = async () => {
    try { posthog.capture("upgrade_clicked", { plan: "pro", from_plan: userPlan }); } catch { /* ignored */ }
    setLoading(true);
    try {
      const data = await api("/api/stripe/create-checkout-session", {
        method: "POST",
        body: JSON.stringify({ product: "pro" }),
      });
      if (data.url) window.location.href = data.url;
    } catch {
      setLoading(false);
    }
  };

  // Each benefit here maps to something the server actually 402-gates — real
  // hourly rate, income forecasts, and documentation score are free today and
  // must NOT appear (CLAUDE.md gating principle; QA 2026-06-09).
  const PRO_BENEFITS = {
    summaries: t('upgrade.proFeature.aiSummaries', 'Care summaries after every shift'),
    weeklyTrends: t('upgrade.proFeature.weeklyTrends', 'Weekly trend reports for families'),
    emailUpdates: t('upgrade.proFeature.emailNotifications', 'Automatic email updates to families'),
    unlimitedClients: t('upgrade.proFeature.unlimitedClients', 'Unlimited clients'),
    unlimitedInvoicing: t('upgrade.proFeature.unlimitedInvoicing', 'Unlimited invoicing with payment reminders'),
    voiceLogging: t('upgrade.proFeature.voiceLogging', 'Log shifts by voice'),
    taxTools: t('upgrade.proFeature.taxTools', 'Tax tools: Schedule C export and payment tracking'),
    careHistoryPdf: t('upgrade.proFeature.careHistoryPdf', 'Care history and year-end PDF reports'),
  } as const;

  // Contextual paywall (ISSUE 3.5): lead with the 3 benefits closest to what
  // the user just tried to do, then link to the full list. Keys are the
  // `feature` values dispatched by carelogic:upgrade-required across the app.
  const CONTEXT_TOP3: Record<string, Array<keyof typeof PRO_BENEFITS>> = {
    ai_summary: ["summaries", "weeklyTrends", "emailUpdates"],
    care_feed: ["summaries", "emailUpdates", "weeklyTrends"],
    family_portal: ["weeklyTrends", "emailUpdates", "summaries"],
    care_history: ["careHistoryPdf", "summaries", "weeklyTrends"],
    care_plan_pdf: ["careHistoryPdf", "summaries", "weeklyTrends"],
    weekly_trends: ["weeklyTrends", "summaries", "emailUpdates"],
    clients: ["unlimitedClients", "unlimitedInvoicing", "summaries"],
    unlimited_clients: ["unlimitedClients", "unlimitedInvoicing", "summaries"],
    invoice_limit: ["unlimitedInvoicing", "taxTools", "unlimitedClients"],
    voice_parse: ["voiceLogging", "summaries", "unlimitedClients"],
  };

  // Default (no context) = the 3 strongest overall.
  const DEFAULT_TOP3: Array<keyof typeof PRO_BENEFITS> = ["summaries", "unlimitedClients", "weeklyTrends"];
  const topKeys = (feature && CONTEXT_TOP3[feature]) || DEFAULT_TOP3;
  const topBenefits = topKeys.map((k) => PRO_BENEFITS[k]);

  return (
    <>
      <div onClick={handleClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", backdropFilter: "blur(4px)", zIndex: 70 }} />
      <div onClick={handleClose} style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 71, display: "flex", alignItems: "center", justifyContent: "center", padding: 16, overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 600, background: "var(--color-surface)", borderRadius: "var(--radius-lg)", padding: 24, boxShadow: "var(--shadow-lg)", position: "relative", margin: "auto 0" }}>
        <button onClick={handleClose} style={{ position: "absolute", top: 12, right: 12, background: "none", border: "none", cursor: "pointer", padding: 4 }}>
          <X size={18} color="var(--color-text-muted)" />
        </button>

        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <div style={{ fontSize: 24, marginBottom: 4 }}>✦</div>
          <h2 style={{ fontSize: 20, fontWeight: 700 }}>{featureCopy ? featureCopy.headline : t('upgrade.defaultHeadline', 'Unlock CareLogic')}</h2>
          {featureCopy
            ? <p style={{ fontSize: 13, color: "var(--color-text-muted)", marginTop: 4 }}>{featureCopy.subtext}</p>
            : reason && <p style={{ fontSize: 13, color: "var(--color-text-muted)", marginTop: 4 }}>{reason}</p>
          }
        </div>

        {!isStarterUser && stats && stats.totalShifts > 0 && (
          <p style={{ fontSize: 13, color: "var(--color-text-muted)", textAlign: "center", fontStyle: "italic", marginBottom: 16, marginTop: -8 }}>
            {t('upgrade.trialStats', 'You have logged {{shifts}} shifts and tracked ${{earned}} in earnings with CareLogic.', { shifts: stats.totalShifts, earned: stats.totalEarned.toFixed(0) })}
            {stats.totalHours > 0 && ` ${t('upgrade.trialHours', 'That is {{hours}} hours on record.', { hours: stats.totalHours.toFixed(1) })}`}
          </p>
        )}

        {monthlyEstimate !== null && monthlyEstimate >= 200 && (() => {
          // monthlyEstimate is weekly earnings x 4.3 — a pace projection, not
          // an invoiced total. The old copy claimed "You've invoiced $X this
          // month", which was false for users who hadn't invoiced anything.
          let copy = t('upgrade.earningsRoi', 'You\'re on pace to earn ${{amount}} this month. Pro is $19/mo', { amount: monthlyEstimate.toLocaleString() });
          if (stats && stats.totalHours > 0) {
            const hourlyRate = stats.totalEarned / stats.totalHours;
            const minutes = Math.round(19 / hourlyRate * 60);
            copy += t('upgrade.earningsRoiMinutes', ' — less than {{minutes}} minutes of your time.', { minutes });
          } else {
            copy += ".";
          }
          return (
            <p style={{ fontSize: 13, background: "rgba(123, 109, 175, 0.08)", border: "1px solid rgba(123, 109, 175, 0.2)", borderRadius: "var(--radius-md)", padding: "10px 14px", marginBottom: 16, textAlign: "center" }}>
              {copy}
            </p>
          );
        })()}

        {(() => {
          const now = new Date();
          const isTaxSeason = now >= new Date(now.getFullYear(), 2, 1) && now <= new Date(now.getFullYear(), 3, 15);
          if (!isTaxSeason) return null;
          return (
            <div style={{ background: "rgba(212, 168, 67, 0.08)", border: "1px solid rgba(212, 168, 67, 0.3)", borderRadius: "var(--radius-md)", padding: "12px 14px", marginBottom: 16 }}>
              <div style={{ display: "inline-block", background: "var(--color-gold)", color: "var(--color-on-gold)", fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", padding: "2px 7px", borderRadius: 4, marginBottom: 8 }}>TAX SEASON</div>
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>{t('upgrade.taxSeasonHeading', 'April 15 is closer than you think.')}</div>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 7, marginBottom: 4 }}>
                <Check size={14} color="var(--color-gold)" style={{ marginTop: 2, flexShrink: 0 }} />
                <span style={{ fontSize: 13 }}>{t('upgrade.taxFeature1', 'Full tax summary with mileage deduction estimate')}</span>
              </div>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 7 }}>
                <Check size={14} color="var(--color-gold)" style={{ marginTop: 2, flexShrink: 0 }} />
                <span style={{ fontSize: 13 }}>{t('upgrade.taxFeature2', 'FICA wage tracking to know when you owe')}</span>
              </div>
            </div>
          );
        })()}

        {/* Contextual benefits (ISSUE 3.5): 3 most relevant to what the user
            just tried, then a link to the full list — 8 checkmarks was more
            than anyone reads at the moment of intent. */}
        <div style={{ marginBottom: 14 }}>
          {topBenefits.map((f) => (
            <div key={f} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0" }}>
              <Check size={15} color="var(--color-success)" style={{ flexShrink: 0 }} />
              <span style={{ fontSize: 14 }}>{f}</span>
            </div>
          ))}
        </div>
        <a
          href="/pricing"
          onClick={handleClose}
          style={{
            display: "inline-block", marginBottom: 20, fontSize: 13, fontWeight: 600,
            color: "var(--color-primary)", textDecoration: "none",
          }}
        >
          {t('upgrade.seeEverything', 'See everything in Pro →')}
        </a>

        <div style={{ textAlign: "center", marginBottom: 12 }}>
          <div style={{ fontSize: 28, fontWeight: 700 }}>$19 <span style={{ fontSize: 14, fontWeight: 400, color: "var(--color-text-muted)" }}>/ month</span></div>
          <div style={{ fontSize: 12, color: "var(--color-text-muted)" }}>{t('upgrade.cancelAnytime', '7-day free trial, then $19/mo. Cancel anytime.')}</div>
        </div>

        <button className="btn-primary" onClick={handleUpgrade} disabled={loading}>
          {loading ? <Loader2 size={18} style={{ animation: "spin 1s linear infinite" }} /> : t('upgrade.subscribe', 'Start 7-day free trial')}
        </button>
        <button onClick={handleClose} style={{ display: "block", width: "100%", textAlign: "center", marginTop: 10, background: "none", border: "none", color: "var(--color-text-muted)", fontSize: 13, cursor: "pointer", padding: "8px 0" }}>{t('upgrade.maybeLater', 'Maybe later')}</button>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
      </div>
    </>
  );
}
